/**
 * Carga de datos reales pre-computados desde /data/*.json.
 *
 * Estos archivos se generan con `scripts/build_real_data.sql` (DuckDB)
 * a partir del parquet real de medidores de Antofagasta (enero 2025,
 * 36.660 medidores, 119 gateways).  Se cargan una sola vez y se
 * cachean; si no existen, las funciones devuelven `null` y el mock
 * cae a los datos sintéticos (degradación elegante).
 *
 * Ubicación de referencia: Antofagasta, Chile (-23.65, -70.40).
 */

export interface RealDma {
  codigo: string;
  nombre: string;
  lat: number;
  lon: number;
  clientes: number;
  volumen_total: number;
  fugas: number;
  backflow: number;
  bateria: number;
  nrw_pct: number;
}

export interface RealGateway {
  gateway_id: string;
  uplinks: number;
  medidores: number;
  avg_rssi: number;
  avg_snr: number;
  avg_sf: number;
}

export interface RealAlarma {
  device: string;
  dma_idx: number;
  total_volume: number;
  alarm_leakage: boolean;
  alarm_burst: boolean;
  alarm_backflow: boolean;
  alarm_dry: boolean;
  alarm_manipulation: boolean;
  alarm_battery: boolean;
  alarm_permanent: boolean;
  root_timestamp: string;
}

export interface RealSf {
  sf: number;
  count: number;
}

export interface RealExecutive {
  medidores: number;
  volumen_total: number;
  leak: number;
  burst: number;
  backflow: number;
  dry: number;
  battery: number;
  permanent: number;
}

/**
 * Válvula / PRV sintética de la Capa, derivada de forma determinista
 * sobre las DMAs reales (R17.2, R17.5). `tipo` distingue `valvula` de `PRV`.
 */
export interface RealValvula {
  valvula_id: string;
  dma_ref: string;
  dma_nombre: string;
  lat: number;
  lon: number;
  tipo: "valvula" | "PRV";
  estado: "abierta" | "cerrada" | "parcial";
  diametro_mm: number;
  setpoint_mca: number | null;
  downstream_dma_ref: string;
}

/** Punto de Presion/MNF sintético por DMA (R17.2). */
export interface RealPresion {
  sensor_id: string;
  dma_ref: string;
  lat: number;
  lon: number;
  presion_mca: number;
  presion_nocturna_mca: number;
  mnf_lps: number;
  ts: string;
}

/** Caso semilla con entidad raíz DMA y los 9 componentes [0,100] del Score_Caso. */
export interface RealCasoSeed {
  caso_id: string;
  entidad_raiz_ref: string;
  entidad_raiz_tipo: string;
  entidad_raiz_nombre: string;
  lat: number;
  lon: number;
  estado: string;
  fecha_creacion: string;
  medidores: number;
  leak: number;
  burst: number;
  backflow: number;
  manipulation: number;
  battery: number;
  permanent: number;
  volumen_total: number;
  comp_impacto_economico: number;
  comp_criticidad: number;
  comp_recurrencia: number;
  comp_presion: number;
  comp_mnf: number;
  comp_anomalias_iot: number;
  comp_cobertura_rf: number;
  comp_sla: number;
  comp_reincidencia: number;
}

/** Documento no estructurado del corpus RAG (los embeddings se calculan client-side). */
export interface RealRagDoc {
  doc_id: string;
  titulo: string;
  entidad_ref: string;
  tipo: string;
  fecha: string;
  autor: string;
  texto: string;
  orden: number;
}

interface RealDataset {
  dmas: RealDma[];
  gateways: RealGateway[];
  alarmas: RealAlarma[];
  sf: RealSf[];
  executive: RealExecutive | null;
  valvulas: RealValvula[];
  presion: RealPresion[];
  casosSeed: RealCasoSeed[];
  ragCorpus: RealRagDoc[];
}

let cache: RealDataset | null = null;
let loadPromise: Promise<RealDataset | null> | null = null;

async function fetchJson<T>(file: string): Promise<T | null> {
  try {
    const res = await fetch(`/data/${file}`, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/**
 * Carga (con cache) todos los datasets reales. Devuelve `null` si no
 * hay datos reales disponibles (ej. archivos no desplegados).
 */
export async function loadRealData(): Promise<RealDataset | null> {
  if (cache) return cache;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const [dmas, gateways, alarmas, sf, executiveArr, valvulas, presion, casosSeed, ragCorpus] =
      await Promise.all([
        fetchJson<RealDma[]>("dmas.json"),
        fetchJson<RealGateway[]>("gateways.json"),
        fetchJson<RealAlarma[]>("alarmas.json"),
        fetchJson<RealSf[]>("sf_distribution.json"),
        fetchJson<RealExecutive[]>("executive.json"),
        fetchJson<RealValvula[]>("valvulas.json"),
        fetchJson<RealPresion[]>("presion.json"),
        fetchJson<RealCasoSeed[]>("casos_seed.json"),
        fetchJson<RealRagDoc[]>("rag_corpus.json"),
      ]);
    if (!dmas || dmas.length === 0) {
      return null; // sin datos reales → mock sintético
    }
    cache = {
      dmas,
      gateways: gateways ?? [],
      alarmas: alarmas ?? [],
      sf: sf ?? [],
      executive: executiveArr?.[0] ?? null,
      valvulas: valvulas ?? [],
      presion: presion ?? [],
      casosSeed: casosSeed ?? [],
      ragCorpus: ragCorpus ?? [],
    };
    return cache;
  })();

  return loadPromise;
}

/** Acceso sincrónico al cache (null si aún no se cargó). */
export function realDataSync(): RealDataset | null {
  return cache;
}

/**
 * Loaders individuales de las entidades nuevas de la Capa, con
 * degradación elegante: devuelven `[]` si no hay datos reales
 * disponibles (mismo patrón que el resto de loaders).
 */
export async function loadValvulas(): Promise<RealValvula[]> {
  return (await loadRealData())?.valvulas ?? [];
}

export async function loadPresion(): Promise<RealPresion[]> {
  return (await loadRealData())?.presion ?? [];
}

export async function loadCasosSeed(): Promise<RealCasoSeed[]> {
  return (await loadRealData())?.casosSeed ?? [];
}

export async function loadRagCorpus(): Promise<RealRagDoc[]> {
  return (await loadRealData())?.ragCorpus ?? [];
}
